import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { LEGAL_CONTENT } from '../lib/legalContent';
import { LEGAL_LABELS } from '../lib/legalDocs';
import { ShieldCheck, FileText, Globe } from 'lucide-react';

/**
 * Renders the Terms & Conditions or Privacy Policy as a normal in-app web page.
 * The user lands here via /legal/terms or /legal/privacy. Content comes from
 * legalContent.js (verbatim from the signed PDFs) and is selected by the
 * current UI language.
 */
export const LegalPage = () => {
  const { type } = useParams(); // "terms" | "privacy"
  const { language, setLanguage } = useLanguage();
  const isRTL = language === 'ar';

  if (type !== 'terms' && type !== 'privacy') {
    return <Navigate to="/" replace />;
  }

  const sections = LEGAL_CONTENT[type][language] || LEGAL_CONTENT[type].en;
  const title = LEGAL_LABELS[type][language] || LEGAL_LABELS[type].en;
  const Icon = type === 'terms' ? FileText : ShieldCheck;

  return (
    <div
      className={`min-h-screen bg-[#f4f7fb] ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid={`legal-page-${type}`}
    >
      {/* Header band */}
      <div className="bg-gradient-to-b from-[#0d47a1] via-[#1565c0] to-[#1e88e5] text-white py-10 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Icon size={28} strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-extrabold" data-testid="legal-page-title">{title}</h1>
            <p className="text-blue-100 text-sm mt-1">
              {language === 'ar' ? 'تاريخ آخر تحديث: 06-2026'
                : language === 'fr' ? 'Dernière mise à jour : 06-2026'
                : 'Last updated: Jun, 2026'}
            </p>
          </div>
          {/* Language switcher (AR / EN) */}
          <div className="flex items-center gap-1 bg-white/15 backdrop-blur rounded-full p-1 text-xs font-bold" data-testid="legal-lang-switcher">
            <Globe size={14} className="ms-2 me-1 opacity-80" />
            <button
              type="button"
              onClick={() => setLanguage('ar')}
              className={`px-3 py-1.5 rounded-full transition ${language === 'ar' ? 'bg-white text-[#0d47a1]' : 'text-white/80 hover:text-white'}`}
              data-testid="legal-lang-ar"
            >
              العربية
            </button>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 rounded-full transition ${language === 'en' ? 'bg-white text-[#0d47a1]' : 'text-white/80 hover:text-white'}`}
              data-testid="legal-lang-en"
            >
              English
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="p-6 md:p-10 rounded-2xl shadow-md border-0 bg-white">
          <article className={`space-y-6 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
            {sections.map((s, i) => (
              <section key={`${s.heading}-${i}`} className="reveal" data-testid={`legal-section-${i}`}>
                <h2 className="text-lg md:text-xl font-extrabold text-[#0d47a1] mb-3 border-b border-blue-100 pb-2">
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, j) => (
                  <p key={`p-${i}-${j}-${p.slice(0, 20)}`} className="text-gray-700 text-sm md:text-base mb-3 whitespace-pre-wrap">
                    {p}
                  </p>
                ))}
                {s.bullets.length > 0 && (
                  <ul className={`list-disc text-gray-700 text-sm md:text-base space-y-1 ${isRTL ? 'pr-5' : 'pl-5'}`}>
                    {s.bullets.map((b, k) => (
                      <li key={`b-${i}-${k}-${b.slice(0, 20)}`}>{b.replace(/^[•\-]\s*/, '')}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </article>

          <div className="mt-10 pt-6 border-t text-center text-xs text-gray-400">
            © {new Date().getFullYear()} Tababah — wecare@tababa.co
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LegalPage;
